import { LegacyRef, useMemo, useState } from 'react'

import { VerticalMenuIcon } from '@ui/components/icons'
import { useLogoPathBySlug } from '@ui/lib/hooks/useLogoPathBySlug'
import { useClickAway } from '@uidotdev/usehooks'
import clsx from 'clsx'
import dayjs from 'dayjs'

export type TableListItem = Record<string, string | string[] | number | boolean | null | undefined>

export type TableListItems<T extends TableListItem = TableListItem> = T[]

export type MenuItem = {
  label: string
  onClick: (item: string) => void
}

/**
 * Props for the TableList component.
 * @template T - Type extending TableListItem
 */
interface TableListProps<T extends TableListItem> {
  /** Array of items to display in the table */
  items: TableListItems<T>
  /** Array of keys from T to be displayed as logo columns */
  logoColumns?: (keyof T)[]
  /** Array of menu items for each row */
  menuItems?: MenuItem[]
  /** Callback function when a row is clicked */
  onRowClick?: (item: string) => void
  /** Minimum width for each column */
  minColumnWidth?: string
}

const isDate = (value: string | number): boolean => {
  // If it's a number, it's not a date
  if (typeof value === 'number') return false

  // Check if it matches a date format (YYYY-MM-DD)
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/
  if (!dateRegex.test(value)) return false

  // Verify it's a valid date using dayjs
  return dayjs(value, 'YYYY-MM-DD', true).isValid()
}

const getUniqueKeys = (items: TableListItems) => {
  const keys = new Set<string>()
  items.forEach((item) => {
    // Hide id column
    Object.keys(item).forEach((key) => {
      if (key !== 'id') {
        keys.add(key)
      }
    })
  })
  return Array.from(keys)
}

const formatItem = (item: string | string[] | number | boolean | null | undefined) => {
  switch (typeof item) {
    case 'string':
      if (isDate(item)) {
        return dayjs(item).format('L LT')
      }
      return item
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    case 'number':
      return item.toString()
    case 'boolean':
      return item ? (
        <p className="w-fit rounded-md bg-success-50 px-2 py-1 text-sm font-medium text-success-600">Enabled</p>
      ) : (
        <p className="w-fit rounded-md bg-negative-50 px-2 py-1 text-sm font-medium text-negative-600">Disabled</p>
      )
    default:
      return item
  }
}

const HeaderItem = ({
  item,
  width,
}: {
  item: string | number | boolean | null | undefined
  width: number | string
}) => {
  const formattedItem = useMemo(() => formatItem(item), [item])

  return (
    <div className="text-sm font-medium text-gray-600" style={{ minWidth: width }}>
      {formattedItem}
    </div>
  )
}

const BodyItem = ({
  item,
  width,
  isLogoColumn,
}: {
  item: string | string[] | number | boolean | null | undefined
  width: number | string
  isLogoColumn?: boolean
}) => {
  const formattedItem = useMemo(() => formatItem(item), [item])
  const slugs = formattedItem && isLogoColumn ? formattedItem : []
  const logoPathBySlug = useLogoPathBySlug(slugs as string[])
  const logoUrls = Object.values(logoPathBySlug)

  if (isLogoColumn) {
    if (!logoUrls.length) {
      return <div className="size-10 rounded-full bg-gray-200"></div>
    }
    return logoUrls.map((item, index) => {
      return (
        <img
          key={index}
          src={item}
          alt="Logo"
          style={{ zIndex: index }}
          className={
            '-ml-6 size-10 rounded-full border border-gray-200 bg-white transition-all duration-300 first:m-0 group-hover:mx-0.5'
          }
        />
      )
    })
  }

  return (
    <div className="text-sm text-gray-800" style={{ width: width }}>
      {formattedItem}
    </div>
  )
}

/**
 * TableList component for displaying data in a tabular format with optional logo columns and row actions.
 *
 * @template T - Type extending TableListItem
 * @param {TableListProps<T>} props - The props for the TableList component
 * @returns {JSX.Element} The rendered TableList component
 *
 * @example
 * // Basic usage
 * <TableList
 *   items={[
 *     { id: '1', name: 'John Doe', age: 30, isActive: true },
 *     { id: '2', name: 'Jane Smith', age: 25, isActive: false }
 *   ]}
 * />
 *
 * @example
 * // With logo columns and menu items
 * <TableList
 *   items={[
 *     { id: '1', name: 'Company A', logo: 'companyA', revenue: 1000000 },
 *     { id: '2', name: 'Company B', logo: 'companyB', revenue: 2000000 }
 *   ]}
 *   logoColumns={['logo']}
 *   menuItems={[
 *     { label: 'Edit', onClick: (id) => console.log('Edit', id) },
 *     { label: 'Delete', onClick: (id) => console.log('Delete', id) }
 *   ]}
 *   onRowClick={(id) => console.log('Row clicked', id)}
 * />
 */

export const TableList = ({
  items,
  logoColumns,
  menuItems,
  onRowClick,
  minColumnWidth,
}: TableListProps<TableListItem>) => {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)
  const [openMenuPosition, setOpenMenuPosition] = useState<{ left: number; top: number } | null>(null)

  const MIN_COLUMN_WIDTH = '20rem'

  const menuRef = useClickAway(() => {
    setOpenMenuIndex(null)
  })

  const headerItems = useMemo(() => {
    const keys = getUniqueKeys(items)
    return Array.from(keys)
  }, [items])

  const bodyItems = useMemo(() => {
    return items.map((item) => {
      return Object.values(item).filter((value) => value !== item.id)
    })
  }, [items])

  const itemIds = useMemo(() => {
    return items.map((item) => item.id)
  }, [items]) as string[]

  return (
    <div className="scrollbar-hide relative overflow-x-scroll rounded-md">
      <table className="w-full table-auto overflow-scroll rounded-md">
        <thead className="sticky top-0 z-10 w-full  bg-gray-200">
          <tr className="bg-gray-200">
            {headerItems.map((item, index) => (
              <th key={index} className="px-4 py-2 text-left text-sm font-medium text-gray-600">
                <HeaderItem item={item} width={minColumnWidth ?? MIN_COLUMN_WIDTH} />
              </th>
            ))}
            <th className="sticky right-0 w-0 bg-gray-200" />
          </tr>
        </thead>
        <tbody className="overflow-hidden  border-x border-gray-200">
          {bodyItems.map((bodyItem, bodyIndex) => (
            <tr
              key={bodyIndex}
              onClick={() => {
                onRowClick && onRowClick(itemIds[bodyIndex])
              }}
              className="group cursor-pointer overflow-hidden border-b border-gray-200 bg-white hover:bg-gray-100">
              {bodyItem.map((bodyValue, bodyValueIndex) => (
                <td key={bodyValueIndex} className="px-4 py-6" style={{ minWidth: minColumnWidth }}>
                  <div style={{ maxWidth: minColumnWidth }} className="flex flex-row overflow-x-auto">
                    <BodyItem
                      item={bodyValue}
                      isLogoColumn={logoColumns?.includes(headerItems[bodyValueIndex] as keyof TableListItem)}
                      width={minColumnWidth ?? MIN_COLUMN_WIDTH}
                    />
                  </div>
                </td>
              ))}
              {menuItems && (
                <td className="sticky right-0 w-0 bg-white group-hover:bg-gray-100">
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      setOpenMenuIndex(bodyIndex)
                      setOpenMenuPosition({
                        left: e.clientX - 150,
                        top: e.clientY + 20,
                      })
                    }}
                    className={clsx(
                      'absolute right-4 top-1/3 z-10 -mt-1 rotate-90 cursor-pointer stroke-gray-900 p-2 opacity-0',
                      'hover:bg-gray-200 group-hover:rounded-md group-hover:opacity-100 group-hover:duration-300 group-hover:ease-in-out',
                      {
                        'opacity-100': openMenuIndex === bodyIndex,
                      }
                    )}>
                    <VerticalMenuIcon className="size-4" />
                  </div>
                  {openMenuIndex === bodyIndex && (
                    <div
                      ref={menuRef as LegacyRef<HTMLDivElement>}
                      style={{
                        position: 'fixed',
                        left: `${openMenuPosition?.left ?? 0}px`,
                        top: `${openMenuPosition?.top ?? 0}px`,
                      }}
                      className="z-10 w-40 cursor-pointer rounded-md border border-gray-200 bg-white stroke-gray-900">
                      {menuItems?.map((item, menuItemIndex) => (
                        <div
                          className="cursor-pointer border-b border-gray-200 p-2 text-xs last:border-b-0 hover:bg-gray-200"
                          key={menuItemIndex}
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            item.onClick(itemIds[bodyIndex])
                            setOpenMenuIndex(null)
                          }}>
                          {item.label}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
